import React from 'react';

import clsx from 'clsx';
import { Check } from 'lucide-react';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { SigningIcon } from 'src/components/icons/SigningIcon';
import { WorkflowSingedIcon } from 'src/components/icons/WorkflowSingedIcon';
import { Apollo } from 'src/gql/generated';
import { StepIconCircle } from 'src/workflow';
import { getWorkflowStateColor } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  connector: {
    borderTop: `2px dashed ${theme.appColors.rule}`,
    flex: 1,
    height: 0,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    marginTop: 16,
    minWidth: 16,
  },
  connectorActive: {
    borderTopColor: theme.palette.primary.main,
  },
  label: {
    fontSize: 10,
    fontWeight: 600,
    left: '50%',
    marginTop: theme.spacing(0.5),
    position: 'absolute' as const,
    top: '100%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap' as const,
  },
  labelActive: {
    color: theme.appColors.iconsType,
  },
  labelInactive: {
    color: 'rgba(50, 74, 82, 0.35)',
  },
  root: {
    alignItems: 'flex-start',
    display: 'flex',
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(1),
    width: '100%',
  },
  step: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative',
  },
  stepCircle: {
    border: `1px solid ${theme.appColors.rule}`,
    color: 'rgba(50, 74, 82, 0.4)',
  },
  stepCircleActive: {
    border: 'none',
    color: theme.palette.common.white,
  },
  stepCircleDone: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.success.main}`,
    color: theme.palette.success.main,
  },
}));

const STEPS: Array<{
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  states: Apollo.WorkflowState[];
}> = [
  {
    Icon: DocumentIcon,
    label: 'Ready to sign',
    states: [Apollo.WorkflowState.ReadyForSignature],
  },
  {
    Icon: SigningIcon,
    label: 'Signing',
    states: [Apollo.WorkflowState.Signing],
  },
  {
    Icon: WorkflowSingedIcon,
    label: 'Signed',
    states: [Apollo.WorkflowState.Signed, Apollo.WorkflowState.Complete],
  },
];

function getActiveStepIndex(workflowState: Apollo.WorkflowState): number {
  return STEPS.findIndex(s => s.states.includes(workflowState));
}

interface SigningProgressStepperProps {
  workflowState: Apollo.WorkflowState;
}

export const SigningProgressStepper = React.memo(function SigningProgressStepper({
  workflowState,
}: SigningProgressStepperProps) {
  const { classes, theme } = useStyles();
  const activeIndex = getActiveStepIndex(workflowState);

  return (
    <div className={classes.root}>
      {STEPS.map((step, index) => {
        const isFirst = index === 0;
        const hasActiveStep = activeIndex >= 0;
        const isActive = hasActiveStep && index === activeIndex;
        const isPast = hasActiveStep && index < activeIndex;
        const isAllDone = STEPS[STEPS.length - 1].states.includes(workflowState);
        const isDone = isPast || isAllDone;

        const connectorBeforeActive = hasActiveStep && activeIndex >= index;

        const circleClassName = isDone
          ? classes.stepCircleDone
          : isActive
            ? classes.stepCircleActive
            : classes.stepCircle;
        const activeColor = isActive ? getWorkflowStateColor(workflowState) : undefined;

        return (
          <React.Fragment key={step.label}>
            {!isFirst && (
              <div
                className={clsx(
                  classes.connector,
                  connectorBeforeActive && classes.connectorActive
                )}
                style={isDone ? { borderTopColor: theme.palette.success.main } : undefined}
              />
            )}
            <div className={classes.step}>
              <StepIconCircle
                className={circleClassName}
                icon={
                  isDone ? (
                    <Check color={theme.palette.success.main} size={16} />
                  ) : (
                    <step.Icon style={{ fontSize: 16 }} />
                  )
                }
                style={{
                  backgroundColor: activeColor,
                  height: 32,
                  width: 32,
                }}
              />
              <span
                className={clsx(
                  classes.label,
                  isActive || isDone ? classes.labelActive : classes.labelInactive
                )}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
});
